import dayjs from "dayjs";

import { supabase } from "~/integrations/supabase/client";
import type {
  ExchangeRate,
  ExchangeRateFilters,
  ExchangeRateFormData,
  ManualOverrideFormData,
} from "~/types/exchangeRates";

export const exchangeRateService = {
  /**
   * Get all exchange rates with optional filtering
   */
  async getAll(filters?: ExchangeRateFilters): Promise<ExchangeRate[]> {
    let query = supabase
      .from("exchange_rates")
      .select("*")
      .order("rate_date", { ascending: false });

    if (filters?.dateFrom) {
      query = query.gte("rate_date", filters.dateFrom);
    }
    if (filters?.dateTo) {
      query = query.lte("rate_date", filters.dateTo);
    }
    if (filters?.source) {
      query = query.eq("source", filters.source);
    }
    if (filters?.isManualOverride !== undefined) {
      query = query.eq("is_manual_override", filters.isManualOverride);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as ExchangeRate[];
  },

  /**
   * Get exchange rate by ID
   */
  async getById(id: string): Promise<ExchangeRate> {
    const { data, error } = await supabase
      .from("exchange_rates")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as ExchangeRate;
  },

  /**
   * Get exchange rate for a specific date
   */
  async getByDate(date: string): Promise<ExchangeRate | null> {
    const { data, error } = await supabase
      .from("exchange_rates")
      .select("*")
      .eq("rate_date", date)
      .single();

    // Ignore "no rows" error
    if (error && error.code !== "PGRST116") throw error;
    return data as ExchangeRate | null;
  },

  /**
   * Get today's exchange rate (or most recent if not available)
   */
  async getToday(): Promise<ExchangeRate | null> {
    const today = dayjs().format("YYYY-MM-DD");

    // Try today first
    const todayRate = await this.getByDate(today);
    if (todayRate) return todayRate;

    // Fall back to most recent
    const { data, error } = await supabase
      .from("exchange_rates")
      .select("*")
      .lte("rate_date", today)
      .order("rate_date", { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== "PGRST116") throw error;
    return data as ExchangeRate | null;
  },

  /**
   * Get exchange rate for date with fallback (calls SQL function)
   */
  async getRateForDate(date: string): Promise<number | null> {
    const { data, error } = await supabase.rpc("get_exchange_rate", {
      target_date: date,
    });

    if (error) throw error;
    return data as number | null;
  },

  /**
   * Convert USD to CLP using rate for date
   */
  async usdToClp(amountUsd: number, date?: string): Promise<number | null> {
    const targetDate = date || dayjs().format("YYYY-MM-DD");
    const { data, error } = await supabase.rpc("usd_to_clp", {
      amount_usd: amountUsd,
      target_date: targetDate,
    });

    if (error) throw error;
    return data as number | null;
  },

  /**
   * Convert CLP to USD using rate for date
   */
  async clpToUsd(amountClp: number, date?: string): Promise<number | null> {
    const targetDate = date || dayjs().format("YYYY-MM-DD");
    const { data, error } = await supabase.rpc("clp_to_usd", {
      amount_clp: amountClp,
      target_date: targetDate,
    });

    if (error) throw error;
    return data as number | null;
  },

  /**
   * Create a manual override for exchange rate
   */
  async createManualOverride(
    formData: ManualOverrideFormData
  ): Promise<ExchangeRate> {
    const { data, error } = await supabase
      .from("exchange_rates")
      .upsert(
        {
          rate_date: formData.rate_date,
          usd_clp: formData.usd_clp,
          source: "manual",
          is_manual_override: true,
          notes: formData.notes || null,
        },
        { onConflict: "rate_date" }
      )
      .select()
      .single();

    if (error) throw error;
    return data as ExchangeRate;
  },

  /**
   * Update an exchange rate
   */
  async update(
    id: string,
    formData: Partial<ExchangeRateFormData>
  ): Promise<ExchangeRate> {
    const { data, error } = await supabase
      .from("exchange_rates")
      .update({
        ...formData,
        is_manual_override: true, // Mark as manual when updated
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as ExchangeRate;
  },

  /**
   * Trigger fetch of exchange rate from API (calls edge function)
   */
  async fetchFromApi(date?: string): Promise<ExchangeRate> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_KEY;

    const url = new URL(`${supabaseUrl}/functions/v1/fetch-exchange-rate`);
    if (date) {
      url.searchParams.set("date", date);
    }

    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${supabaseKey}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch exchange rate");
    }

    const result = await response.json();
    return result.data as ExchangeRate;
  },

  /**
   * Get rate variation between two dates
   */
  async getRateVariation(
    fromDate: string,
    toDate: string
  ): Promise<{
    from: number;
    to: number;
    change: number;
    changePercent: number;
  } | null> {
    const fromRate = await this.getRateForDate(fromDate);
    const toRate = await this.getRateForDate(toDate);

    if (fromRate === null || toRate === null) return null;

    const change = toRate - fromRate;
    const changePercent = (change / fromRate) * 100;

    return {
      from: fromRate,
      to: toRate,
      change,
      changePercent,
    };
  },

  /**
   * Get recent rates for chart/history display
   */
  async getRecentRates(days: number = 30): Promise<ExchangeRate[]> {
    const fromDate = dayjs().subtract(days, "day").format("YYYY-MM-DD");

    const { data, error } = await supabase
      .from("exchange_rates")
      .select("*")
      .gte("rate_date", fromDate)
      .order("rate_date", { ascending: true });

    if (error) throw error;
    return data as ExchangeRate[];
  },
};
